import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Languages.css";
import greetingsImg from "../assets/languages/greetings.png";
import numbersImg from "../assets/languages/numbers.png";

const LANGUAGE = {
  SINHALA: "sinhala",
  TAMIL: "tamil",
};

const MODE = {
  NONE: "none",
  PHRASES: "phrases",
  NUMBERS: "numbers",
};

const languageContent = {
  phrases: [
    {
      id: "hello",
      label: "Welcome",
      sinhala: { script: "ආයුබෝවන්", roman: "āyubōvan", meaning: "Welcome" },
      tamil: { script: "வணக்கம்", roman: "Vāṉakam", meaning: "Welcome" },
    },
    {
      id: "myNameIs",
      label: "My name is…",
      sinhala: { script: "මගේ නම වන්නේ …", roman: "Magē nama vannē …", meaning: "My name is..." },
      tamil: { script: "என் பெயர் …", roman: "En peyar …", meaning: "My name is..." },
    },
    {
      id: "howAreYou",
      label: "How are you?",
      sinhala: { script: "කොහොමද?", roman: "Kohomada?", meaning: "How are you?" },
      tamil: {
        script: "எப்படி இருக்கிறீர்கள்?",
        roman: "eppaṭi irukkiṟīrkaḷ?",
        meaning: "How are you?",
      },
    },
    {
      id: "doingWell",
      label: "I am good",
      sinhala: { script: "මම හොඳින්", roman: "Mama hŏdin", meaning: "I am fine" },
      tamil: { script: "நான் நல்லா இருக்கிறேன்", roman: "Nāṉ nallā irukkuṟēṉ", meaning: "I am fine" },
    },
    {
      id: "goodbye",
      label: "Goodbye",
      sinhala: { script: "ආයුබෝවන්", roman: "āyubōvan", meaning: "Goodbye" },
      tamil: { script: "போய்ட்டு வரேன்", roman: "Pōyṭṭu varēṉ", meaning: "Goodbye" },
    },
  ],
  numbers: [
    { n: 1, sinhala: { script: "එක", roman: "eka", meaning: "one" }, tamil: { script: "ஒன்று", roman: "Oṉṟu", meaning: "one" } },
    { n: 2, sinhala: { script: "දෙක", roman: "deka", meaning: "two" }, tamil: { script: "இரண்டு", roman: "Iraṇṭu", meaning: "two" } },
    { n: 3, sinhala: { script: "තුන", roman: "tuna", meaning: "three" }, tamil: { script: "மூன்று", roman: "Mūṉṟu", meaning: "three" } },
    { n: 4, sinhala: { script: "හතර", roman: "hatara", meaning: "four" }, tamil: { script: "நான்கு", roman: "Nāṉku", meaning: "four" } },
    { n: 5, sinhala: { script: "පහ", roman: "paha", meaning: "five" }, tamil: { script: "ஐந்து", roman: "Aintu", meaning: "five" } },
    { n: 6, sinhala: { script: "හය", roman: "haya", meaning: "six" }, tamil: { script: "ஆறு", roman: "Āṟu", meaning: "six" } },
    { n: 7, sinhala: { script: "හත", roman: "hatha", meaning: "seven" }, tamil: { script: "ஏழு", roman: "Ēḻu", meaning: "seven" } },
    { n: 8, sinhala: { script: "අට", roman: "aṭa", meaning: "eight" }, tamil: { script: "எட்டு", roman: "Eṭṭu", meaning: "eight" } },
    { n: 9, sinhala: { script: "නවය", roman: "navaya", meaning: "nine" }, tamil: { script: "ஒன்பது", roman: "Oṉpatu", meaning: "nine" } },
    { n: 10, sinhala: { script: "දහය", roman: "dahaya", meaning: "ten" }, tamil: { script: "பத்து", roman: "Pattu", meaning: "ten" } },
  ],
};

function getLanguageFolder(language) {
  return language === LANGUAGE.SINHALA ? "sinhala" : "tamil";
}

function getAudioSrc(mode, language, item) {
  if (!item) return "";

  const audioFolder = getLanguageFolder(language);

  // the audio files are named differently for phrases and numbers
  if (mode === MODE.PHRASES) return `/audio/${audioFolder}/${item.id}.mp3`;
  if (mode === MODE.NUMBERS) return `/audio/${audioFolder}/num-${item.n}.mp3`;

  return "";
}

let activeAudio = null;

function playClip(src) {
  if (!src) return;

  // stop the old clip first so two voices do not play over each other
  if (activeAudio) {
    activeAudio.pause();
    activeAudio.currentTime = 0;
  }

  activeAudio = new Audio(src);
  activeAudio.preload = "auto";

  const playRequest = activeAudio.play();
  if (playRequest && typeof playRequest.catch === "function") {
    playRequest.catch(() => {});
  }
}

function HomeButton({ goToPage }) {
  return (
    <button
      className="homeBtn"
      onClick={() => goToPage("/loading", { state: { to: "/home" } })}
      aria-label="Go Home"
      title="Go Home"
      type="button"
    >
      <svg
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="homeBtn__icon"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
          fill="#ffffff"
        />
      </svg>
    </button>
  );
}

export default function Languages() {
  const goToPage = useNavigate();
  const [language, setLanguage] = useState(LANGUAGE.SINHALA);
  const [mode, setMode] = useState(MODE.NONE);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    // this tells the background music to pause while the voice clips are being used
    localStorage.setItem("exploreLankaPauseMusicForVoice", "true");
    window.dispatchEvent(new Event("storage"));

    return () => {
      localStorage.setItem("exploreLankaPauseMusicForVoice", "false");
      window.dispatchEvent(new Event("storage"));
    };
  }, []);

  const flashCards = useMemo(() => {
    const sourceItems =
      mode === MODE.PHRASES
        ? languageContent.phrases
        : mode === MODE.NUMBERS
          ? languageContent.numbers
          : [];

    // this turns both phrases and numbers into the same card shape for the viewer
    return sourceItems.map((item) => {
      const languageText = item[language];
      const heading = mode === MODE.NUMBERS ? `Number ${item.n}` : item.label;

      return {
        key: item.id ?? `num-${item.n}`,
        heading,
        script: languageText.script,
        roman: languageText.roman,
        meaning: languageText.meaning,
        audioSrc: getAudioSrc(mode, language, item),
      };
    });
  }, [mode, language]);

  const activeCard = flashCards[cardIndex];

  function chooseMode(nextMode) {
    // starting at the first card makes it less confusing when switching sets
    setMode(nextMode);
    setCardIndex(0);
  }

  function showNextCard() {
    if (!flashCards.length) return;
    setCardIndex((previousIndex) => (previousIndex + 1) % flashCards.length);
  }

  function showPreviousCard() {
    if (!flashCards.length) return;
    setCardIndex(
      (previousIndex) => (previousIndex - 1 + flashCards.length) % flashCards.length
    );
  }

  return (
    <div className="langPage">
      <section className="langSection">
        <div className="langCard">
          <div className="langHeaderRow">
            <div className="langHeaderText">
              <div className="langHomeRow">
                <HomeButton goToPage={goToPage} />
              </div>
            </div>

            <button
              className="langToggle"
              onClick={() =>
                setLanguage((previousLanguage) =>
                  previousLanguage === LANGUAGE.SINHALA ? LANGUAGE.TAMIL : LANGUAGE.SINHALA
                )
              }
              aria-label="Toggle language"
              title="Toggle language"
            >
              <span className={`pill ${language === LANGUAGE.SINHALA ? "active" : ""}`}>
                Sinhala
              </span>
              <span className={`pill ${language === LANGUAGE.TAMIL ? "active" : ""}`}>
                Tamil
              </span>
            </button>
          </div>

          <div className={`modeRow ${mode !== MODE.NONE ? "active" : ""}`}>
            <button
              className={`modeBtn ${mode === MODE.PHRASES ? "selected" : ""}`}
              onClick={() => chooseMode(MODE.PHRASES)}
            >
              <img
                src={greetingsImg}
                alt="Greetings"
                className="modeBtnImage"
              />
              <span>Useful Phrases / Greetings</span>
            </button>

            <button
              className={`modeBtn ${mode === MODE.NUMBERS ? "selected" : ""}`}
              onClick={() => chooseMode(MODE.NUMBERS)}
            >
              <img
                src={numbersImg}
                alt="Numbers"
                className="modeBtnImage"
              />
              <span>Numbers 1–10</span>
            </button>
          </div>

          {mode === MODE.NONE ? (
            <div className="emptyState">
              <div className="emptyTitle">
                Pick a set and toggle the language to start!
              </div>
            </div>
          ) : (
            <div className="viewerWrap">
              <div className="viewerTop">
                <button
                  className="listenBtn"
                  onClick={() => playClip(activeCard?.audioSrc)}
                  disabled={!activeCard?.audioSrc}
                  title="Listen"
                  aria-label="Listen"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 64 64"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#ffffff"
                    style={{ display: "block", flexShrink: 0 }}
                  >
                    <path d="M59.998,28.001h-7.999c-2.211,0-4,1.789-4,4s1.789,4,4,4h7.999c2.211,0,4-1.789,4-4S62.209,28.001,59.998,28.001z"/>
                    <path d="M49.71,19.466l6.929-4c1.914-1.105,2.57-3.551,1.461-5.465c-1.102-1.914-3.547-2.57-5.46-1.465l-6.93,4c-1.914,1.105-2.57,3.551-1.461,5.464C45.351,19.915,47.796,20.571,49.71,19.466z"/>
                    <path d="M56.639,48.535l-6.929-3.999c-1.914-1.105-4.355-0.449-5.461,1.464c-1.105,1.914-0.453,4.359,1.461,5.465l6.93,4c1.913,1.105,4.358,0.449,5.464-1.465S58.553,49.641,56.639,48.535z"/>
                    <path d="M37.53,0.307c-1.492-0.625-3.211-0.277-4.359,0.867L18.343,16.001H4c-2.211,0-4,1.789-4,4v24C0,46.211,1.789,48,4,48h14.343l14.828,14.828C33.937,63.594,34.96,64,35.999,64c0.516,0,1.035-0.098,1.531-0.305c1.496-0.617,2.469-2.078,2.469-3.695V4.001C39.999,2.384,39.026,0.924,37.53,0.307z"/>
                  </svg>
                  Listen
                </button>
              </div>

              <div className="flashCard" role="group" aria-label="Language card">
                <div className="flashMeta">
                  <span className="flashEnglish">{activeCard?.heading}</span>
                  <span className="flashPronounced">{activeCard?.roman}</span>
                  <span className="flashScript">{activeCard?.script}</span>
                </div>
              </div>

              <div className="navRow">
                <button
                  className="navBtn"
                  onClick={showPreviousCard}
                  disabled={!flashCards.length}
                >
                  ← Previous
                </button>
                <button
                  className="navBtn primary"
                  onClick={showNextCard}
                  disabled={!flashCards.length}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}